import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AiBudgetService } from '../../services/ai-budget/ai-budget.service';
import { AiBudgetSnapshot } from '../../services/ai-budget/ai-budget.types';
import { ToasterService } from '../../services/toaster/toaster.service';

@Component({
  selector: 'app-edit-org-budget-modal',
  templateUrl: './edit-org-budget-modal.component.html',
  styleUrls: ['./edit-org-budget-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditOrgBudgetModalComponent implements OnChanges, OnDestroy {
  @Input() organizationId!: number;
  @Input() currentSnapshot: AiBudgetSnapshot | null = null;
  @Input() open = false;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<AiBudgetSnapshot>();

  capUsdInput: number | null = null;
  isSaving = false;
  errorMessage: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly aiBudgetService: AiBudgetService,
    private readonly toaster: ToasterService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue === true) {
      this.capUsdInput = this.currentSnapshot?.capUsd ?? null;
      this.errorMessage = null;
      this.isSaving = false;
    }
  }

  onSave(): void {
    if (this.capUsdInput == null || this.capUsdInput < 0) {
      this.errorMessage = 'Enter a budget amount of zero or greater.';
      this.cdr.markForCheck();
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.aiBudgetService
      .setOrgBudget(this.organizationId, this.capUsdInput)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (snapshot) => {
          this.saved.emit(snapshot);
          this.closed.emit();
          this.isSaving = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.toaster.warning('Failed to update AI budget');
          this.isSaving = false;
          this.errorMessage = 'Save failed. Try again.';
          this.cdr.markForCheck();
        },
      });
  }

  onClose(): void {
    if (!this.isSaving) {
      this.closed.emit();
    }
  }

  onBackdropClick(): void {
    this.onClose();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
